'use client';

import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { LuMaximize, LuMinimize } from 'react-icons/lu';

import Spinner from '@/components/@base/spinner';
import XImage from '@/components/@base/x-image';

import imagesList from '@/constant/images-list';

interface VideoPlayerProps {
  src: string;
  poster: string;
  title?: string;
}

export default function VideoPlayer({ src, poster, title }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isLoadingRef = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [aspectRatio, setAspectRatio] = useState<'16/9' | '9/16'>('16/9');
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!poster) {
      loadAndPlay();
    }
  }, [poster, src]);

  useEffect(() => {
    if (poster) {
      const img = new Image();
      img.src = poster;
      img.onload = () => {
        setAspectRatio(img.height > img.width ? '9/16' : '16/9');
      };
    }
  }, [poster]);

  /* ================= fullscreen ================= */
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  /* ================= helpers ================= */
  const loadAndPlay = async () => {
    if (!videoRef.current || isLoadingRef.current) return;

    isLoadingRef.current = true;
    setIsLoading(true);

    if (videoRef.current.src !== src) {
      videoRef.current.src = src;
    }

    try {
      await videoRef.current.play();

      // Delay for first frame preparation
      setTimeout(() => {
        setIsLoaded(true);
        setIsPlaying(true);
      }, 150);
    } catch (err) {
      console.warn('Video play failed:', err);
    }

    isLoadingRef.current = false;
    setIsLoading(false);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;

    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current || !videoRef.current.duration) return;
    setProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
  };

  const handleSeek = (value: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = (value / 100) * videoRef.current.duration;
  };

  const handleMetadata = () => {
    if (!videoRef.current) return;
    const { videoWidth, videoHeight } = videoRef.current;
    setAspectRatio(videoHeight > videoWidth ? '9/16' : '16/9');
  };

  const toggleFullscreen = () => {
    if (!videoRef.current) return;

    if (!document.fullscreenElement) {
      videoRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  /* ================= render ================= */
  return (
    <div
      className={clsx(
        'relative mx-auto flex items-center justify-center overflow-hidden rounded-2xl!',
        'min-h-[160px] min-w-[280px]',
        aspectRatio === '9/16'
          ? 'aspect-[9/16] max-h-[80vh] min-h-[60vh]'
          : 'aspect-video max-h-[60vh] max-w-[80vw] min-w-[350px] md:min-w-[600px]',
      )}
    >
      {/* ================= VIDEO ================= */}
      <video
        ref={videoRef}
        preload='none'
        playsInline
        onLoadedMetadata={handleMetadata}
        onTimeUpdate={handleTimeUpdate}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
        aria-label={title}
        className={clsx(
          'absolute inset-0 h-full w-full rounded-2xl! object-cover',
          'transition-opacity duration-700',
          isLoaded ? 'opacity-100' : 'opacity-0',
        )}
      />
      {/* ================= LOADING (NO POSTER) ================= */}
      {!poster && !isLoaded && (
        <div className='absolute inset-0 z-10 flex items-center justify-center'>
          <Spinner />
        </div>
      )}
      {/* ================= COVER ================= */}
      {poster && !isLoaded && (
        <div
          className={clsx(
            'absolute inset-0 z-10 transition-all duration-700',
            'rounded-2xl',
            isLoaded ? 'pointer-events-none opacity-0 blur-md' : 'blur-0 opacity-100',
          )}
        >
          <XImage
            src={poster}
            alt={title || 'video'}
            fill
            className='rounded-2xl object-contain'
          />

          {!isLoaded && (
            <button
              onClick={loadAndPlay}
              disabled={isLoading}
              className='absolute inset-0 flex items-center justify-center'
            >
              <div className='relative flex h-10 w-10 items-center justify-center'>
                <div className='absolute inset-0 animate-ping rounded-full bg-[linear-gradient(180deg,#FEA000_0%,#FF6B01_100%)]' />
                <div className='relative z-10 flex h-10 w-10 items-center justify-center rounded-full bg-[linear-gradient(180deg,#FEA000_10%,#FF6B01_100%)]'>
                  <XImage
                    src={imagesList?.icons?.play}
                    alt='play'
                    width={12}
                    height={12}
                    className='ml-[2px]'
                  />
                </div>
              </div>
            </button>
          )}
        </div>
      )}

      {/* ================= CONTROLS ================= */}
      {isLoaded && (
        <div className='absolute right-3 bottom-3 left-3 z-20 rounded-xl bg-black/40 p-2 backdrop-blur-sm'>
          <div className='flex items-center gap-3'>
            <button onClick={toggleFullscreen} className='w-6 text-white'>
              {isFullscreen ? <LuMinimize size={16} /> : <LuMaximize size={16} />}
            </button>

            <div dir='ltr' className='flex-1'>
              <input
                type='range'
                min={0}
                max={100}
                value={progress}
                onChange={(e) => handleSeek(+e.target.value)}
                className='w-full accent-white'
              />
            </div>

            <button onClick={togglePlay} className='w-6 text-white'>
              {isPlaying ? (
                <XImage src={imagesList?.icons?.pause} alt='pause' width={14} height={14} />
              ) : (
                <XImage src={imagesList?.icons?.play} alt='play' width={12} height={12} />
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
